import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, XCircle, Clock, Users, Gift, AlertTriangle } from 'lucide-react';

interface RewardVerificationData {
  rewardCode: string;
  email: string;
  annotationCount: number;
  rewardEligible: boolean;
  rewardClaimed: boolean;
  rewardClaimedAt: string | null;
  createdAt: string;
  completedSessions: number;
  avgListeningPercentage: number;
  meetsListeningRequirement: boolean;
}

interface SummaryStats {
  totalParticipants: number;
  eligibleParticipants: number;
  meetsListeningReq: number;
  fullyEligible: number;
  claimed: number;
  pending: number;
  totalAnnotations: number;
  avgAnnotationsPerParticipant: number;
  listeningRequirement: number;
}

export function RewardVerificationPage() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<{
    summary: SummaryStats;
    eligibleParticipants: RewardVerificationData[];
    allParticipants: RewardVerificationData[];
    generatedAt: string;
  } | null>(null);
  const [updatingClaimed, setUpdatingClaimed] = useState<Set<string>>(new Set());

  const token = searchParams.get('token');

  const fetchData = React.useCallback(() => {
    if (!token) {
      setError('Verification token is required');
      setLoading(false);
      return;
    }

    setLoading(true);
    fetch(`/api/verification/rewards?token=${encodeURIComponent(token)}`)
      .then(res => {
        if (!res.ok) {
          throw new Error('Invalid token or unauthorized');
        }
        return res.json();
      })
      .then(data => {
        console.log('Fetched verification data:', data);
        setData(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching data:', err);
        setError(err.message);
        setLoading(false);
      });
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleClaimed = async (rewardCode: string, currentStatus: boolean) => {
    if (!token) return;

    console.log('Toggling claimed status:', { rewardCode, currentStatus, newStatus: !currentStatus });
    setUpdatingClaimed(prev => new Set(prev).add(rewardCode));

    try {
      const response = await fetch(`/api/verification/rewards?token=${encodeURIComponent(token)}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          rewardCode,
          claimed: !currentStatus,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Server error:', errorText);
        throw new Error('Failed to update claimed status');
      }

      const result = await response.json();
      console.log('Update result:', result);

      // Refresh data
      await fetchData();
    } catch (err) {
      console.error('Error updating claimed status:', err);
      alert(`Failed to update claimed status: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setUpdatingClaimed(prev => {
        const next = new Set(prev);
        next.delete(rewardCode);
        return next;
      });
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const { summary, eligibleParticipants, allParticipants, generatedAt } = data;

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-2">Tensorpool Reward Verification</h1>
        <p className="text-muted-foreground">
          LAION Music Annotation Platform - Participant Rewards
        </p>
        <p className="text-sm text-muted-foreground mt-2">
          Generated: {new Date(generatedAt).toLocaleString()}
        </p>
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4" />
              Total Participants
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.totalParticipants}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {summary.totalAnnotations} total annotations
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              Fully Eligible
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{summary.fullyEligible}</div>
            <p className="text-xs text-muted-foreground mt-1">
              2+ annotations & {summary.listeningRequirement}% listening
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Gift className="h-4 w-4 text-blue-600" />
              Claimed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{summary.claimed}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Rewards already claimed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4 text-orange-600" />
              Pending
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{summary.pending}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Awaiting reward claim
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Eligible Participants */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            Eligible Participants (Unclaimed)
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Participants who have completed 2+ annotations with {summary.listeningRequirement}%+ average listening (based on top 2 annotations) and haven't claimed their reward yet
          </p>
        </CardHeader>
        <CardContent>
          {eligibleParticipants.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">
              No unclaimed eligible participants
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">Reward Code</th>
                    <th className="text-left p-2">Email</th>
                    <th className="text-center p-2">Annotations</th>
                    <th className="text-center p-2">Sessions</th>
                    <th className="text-center p-2">Avg Listening %</th>
                    <th className="text-center p-2">Created</th>
                    <th className="text-center p-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {eligibleParticipants.map((participant, index) => {
                    const isUpdating = updatingClaimed.has(participant.rewardCode);
                    return (
                      <tr key={index} className="border-b hover:bg-muted/50">
                        <td className="p-2 font-mono text-sm font-bold">{participant.rewardCode}</td>
                        <td className="p-2 font-mono text-sm">{participant.email}</td>
                        <td className="text-center p-2">
                          <span className="inline-flex items-center gap-1">
                            {participant.annotationCount}
                            {participant.rewardEligible && (
                              <CheckCircle className="h-3 w-3 text-green-600" />
                            )}
                          </span>
                        </td>
                        <td className="text-center p-2">{participant.completedSessions}</td>
                        <td className="text-center p-2">
                          <span className={participant.meetsListeningRequirement ? 'text-green-600 font-semibold' : ''}>
                            {participant.avgListeningPercentage.toFixed(1)}%
                          </span>
                        </td>
                        <td className="text-center p-2 text-sm text-muted-foreground">
                          {new Date(participant.createdAt).toLocaleDateString()}
                        </td>
                        <td className="text-center p-2">
                          <button
                            onClick={() => toggleClaimed(participant.rewardCode, false)}
                            disabled={isUpdating}
                            className="px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1 mx-auto"
                            title="Mark as claimed"
                          >
                            {isUpdating ? (
                              <>
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                <span>Claiming...</span>
                              </>
                            ) : (
                              <>
                                <Gift className="h-3 w-3" />
                                <span>Mark Claimed</span>
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* All Participants */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            All Participants with Email
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Complete list of all participants who provided an email address
          </p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Reward Code</th>
                  <th className="text-left p-2">Email</th>
                  <th className="text-center p-2">Annotations</th>
                  <th className="text-center p-2">Sessions</th>
                  <th className="text-center p-2">Avg Listening %</th>
                  <th className="text-center p-2">Eligible</th>
                  <th className="text-center p-2">Claimed</th>
                  <th className="text-center p-2">Created</th>
                </tr>
              </thead>
              <tbody>
                {allParticipants.map((participant, index) => {
                  const isUpdating = updatingClaimed.has(participant.rewardCode);
                  return (
                    <tr key={index} className="border-b hover:bg-muted/50">
                      <td className="p-2 font-mono text-sm font-bold">{participant.rewardCode}</td>
                      <td className="p-2 font-mono text-sm">{participant.email}</td>
                      <td className="text-center p-2">{participant.annotationCount}</td>
                      <td className="text-center p-2">{participant.completedSessions}</td>
                      <td className="text-center p-2">
                        <span className={participant.meetsListeningRequirement ? 'text-green-600 font-semibold' : 'text-muted-foreground'}>
                          {participant.avgListeningPercentage.toFixed(1)}%
                        </span>
                      </td>
                      <td className="text-center p-2">
                        {participant.rewardEligible && participant.meetsListeningRequirement ? (
                          <CheckCircle className="h-4 w-4 text-green-600 mx-auto" />
                        ) : (
                          <XCircle className="h-4 w-4 text-muted-foreground mx-auto" />
                        )}
                      </td>
                      <td className="text-center p-2">
                        <div className="flex flex-col items-center gap-1">
                          <button
                            onClick={() => toggleClaimed(participant.rewardCode, participant.rewardClaimed)}
                            disabled={isUpdating}
                            className="hover:scale-110 transition-transform disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                            title={participant.rewardClaimed ? "Click to mark as unclaimed" : "Click to mark as claimed"}
                          >
                            {isUpdating ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                            ) : participant.rewardClaimed ? (
                              <CheckCircle className="h-5 w-5 text-blue-600" />
                            ) : (
                              <XCircle className="h-5 w-5 text-muted-foreground hover:text-blue-400" />
                            )}
                          </button>
                          {participant.rewardClaimed && participant.rewardClaimedAt && (
                            <span className="text-xs text-muted-foreground">
                              {new Date(participant.rewardClaimedAt).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="text-center p-2 text-sm text-muted-foreground">
                        {new Date(participant.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Legend</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="space-y-2">
            <p className="font-semibold">Status Indicators:</p>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span>Meets requirement (2+ annotations & {summary.listeningRequirement}%+ listening)</span>
            </div>
            <div className="flex items-center gap-2">
              <XCircle className="h-4 w-4 text-muted-foreground" />
              <span>Does not meet requirement</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-blue-600" />
              <span>Reward claimed (click to toggle)</span>
            </div>
            <div className="flex items-center gap-2">
              <XCircle className="h-4 w-4 text-muted-foreground" />
              <span>Reward not claimed (click to toggle)</span>
            </div>
          </div>
          <div className="p-3 bg-blue-50 dark:bg-blue-950/50 rounded-lg">
            <p className="font-semibold mb-1">Reward Details:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>$10 per annotation (max 2 annotations = $20 total)</li>
              <li>Equivalent to 20 hours of H100 GPU time on Tensorpool</li>
              <li>Top 2 annotations must average {summary.listeningRequirement}%+ listening</li>
              <li>Extra annotations don't affect eligibility (only top 2 count)</li>
              <li>Participants provide reward code in Discord (email stays private)</li>
              <li>Match reward code to email for credit transfer</li>
            </ul>
          </div>
          <div className="p-3 bg-green-50 dark:bg-green-950/50 rounded-lg">
            <p className="font-semibold mb-1">💡 How to Use:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>Click the icon in the "Claimed" column to toggle status</li>
              <li>Blue checkmark = reward has been claimed</li>
              <li>Gray X = reward not yet claimed</li>
              <li>Claimed date is automatically recorded</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
